import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import '../chat.scss';
import { useChatSocket } from '../context/useChatSocket';
import { ChatNotification } from '../types';
import { Avatar } from './common/Avatar';
import { ChatIcon } from './common/ChatIcon';

interface CardProps {
  notif: ChatNotification;
  onDismiss: () => void;
  onOpen: () => void;
  onReply: (text: string) => void;
}

const NotificationCard: FC<CardProps> = ({ notif, onDismiss, onOpen, onReply }) => {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState('');

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onReply(t);
    onDismiss();
  };

  const title = notif.isChannel ? `#${notif.channelName || 'kanal'}` : `${notif.senderName || ''} ${notif.senderSurname || ''}`.trim() || 'Yeni mesaj';

  return (
    <motion.div
      className="chat-notif"
      initial={{ opacity: 0, x: 70, scale: 0.92 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 70, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
    >
      <div className="chat-notif__head">
        <Avatar id={notif.senderId} name={notif.senderName} surname={notif.senderSurname} size={38} channel={notif.isChannel} channelName={notif.channelName} />
        <div className="chat-notif__meta" onClick={onOpen}>
          <span className="chat-notif__name">{title}</span>
          {notif.isChannel && (
            <span className="chat-notif__sub">
              {notif.senderName} {notif.senderSurname}
            </span>
          )}
        </div>
        <button type="button" className="chat-notif__close" onClick={onDismiss} title="Kapat">
          <ChatIcon name="close" className="svg-icon-5 svg-icon-gray-500" />
        </button>
      </div>

      <div className="chat-notif__body" onClick={onOpen}>
        {notif.content || '📎 Ek'}
      </div>

      {replying ? (
        <div className="chat-notif__reply">
          <input
            autoFocus
            value={text}
            placeholder="Yanıt yaz…"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                send();
              }
              if (e.key === 'Escape') setReplying(false);
            }}
          />
          <button type="button" onClick={send} title="Gönder">
            <ChatIcon name="send" className="svg-icon-3" svgClassName="chat-send-svg" />
          </button>
        </div>
      ) : (
        <div className="chat-notif__actions">
          <button type="button" className="chat-notif__reply-btn" onClick={() => setReplying(true)}>
            Cevapla
          </button>
          <button type="button" className="chat-notif__open-btn" onClick={onOpen}>
            Aç
          </button>
        </div>
      )}

      {/* 8sn geri sayım — ters progress bar (yanıt yazarken durur) */}
      <div className="chat-notif__bar" style={{ animationPlayState: replying ? 'paused' : 'running' }} onAnimationEnd={onDismiss} />
    </motion.div>
  );
};

export const ChatNotifications: FC = () => {
  const { notifications, dismissNotification, sendMessage, openConversation } = useChatSocket();
  const history = useHistory();

  const open = (n: ChatNotification) => {
    openConversation(n.conversationId);
    history.push('/messages/chat');
    dismissNotification(n.key);
  };

  return (
    <div className="chat-notifs">
      <AnimatePresence>
        {notifications.map((n) => (
          <NotificationCard
            key={n.key}
            notif={n}
            onDismiss={() => dismissNotification(n.key)}
            onOpen={() => open(n)}
            onReply={(text) => sendMessage(n.conversationId, { content: text })}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};
